import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * RmsProviderErrorBoundary
 * ------------------------
 * Error boundary for the RMS provider triage journey (dashboard, patient
 * overview, triage proforma + decision wizard, documents dashboard, document
 * review).
 *
 * Why:
 *   React unmounts the whole tree below the nearest boundary on a render
 *   error. None of the provider triage pages had a boundary, so any render
 *   crash left the clinician on a fully blank screen with no way forward but a
 *   reload — which also discards an in-progress triage decision (the wizard
 *   holds its answers in memory until submit). UAT reported exactly this:
 *   "blank screen, then sends me right back to the beginning".
 *
 * What:
 *   Catches render/lifecycle errors from its children, logs them via
 *   console.error (the established pattern — see RmsFormErrorBoundary), and
 *   shows a recovery panel instead of a dead screen. The panel includes the
 *   underlying error message so a screenshot of the failure identifies the
 *   root cause without needing devtools access.
 *
 * Variants:
 *   - default (page): full-page centred panel with "Try again" and a link back
 *     to the provider dashboard. Used to wrap whole route elements.
 *   - inline: a compact panel with "Try again" only. Used inside the decision
 *     wizards around the current step, so a crashing step never unmounts the
 *     wizard — the answers on every step survive and Back/Next stay usable.
 *
 * "Try again" re-renders the children in place (no reload), so wizard and page
 * state above the boundary are preserved.
 */
export class RmsProviderErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[RmsProvider] Render crash caught by error boundary:', error, info);
  }

  handleRetry() {
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const detail = String(error?.message || error);

    if (this.props.inline) {
      return (
        <div role="alert" className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-4 text-left">
          <p className="text-sm font-semibold text-red-700">This step could not be displayed</p>
          <p className="text-sm text-gray-700">
            Your answers on the other steps are not lost. Try again, or use Back to return to a
            previous step. If this keeps happening, send a screenshot of this message to support.
          </p>
          <p className="break-words rounded bg-white px-2 py-1 font-mono text-xs text-gray-500">{detail}</p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Try again
          </button>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div role="alert" className="w-full max-w-md space-y-3 rounded-lg border border-red-200 bg-white p-6 text-left">
          <h2 className="text-lg font-bold text-red-700">Something went wrong</h2>
          <p className="text-sm text-gray-700">
            This page could not be displayed. Anything you had already submitted is saved. Try
            again, or return to the dashboard. If this keeps happening, send a screenshot of this
            message to support.
          </p>
          <p className="break-words rounded bg-gray-50 px-2 py-1 font-mono text-xs text-gray-500">{detail}</p>
          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={this.handleRetry}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              Try again
            </button>
            <a
              href="/rms/provider/dashboard"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Return to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Route-level wrapper: keys the boundary by the current location so navigating
 * to another page (or another patient) automatically clears a caught error
 * instead of showing a stale failure panel for the new page.
 */
export default function RmsProviderPageErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <RmsProviderErrorBoundary key={`${location.pathname}${location.search}`}>
      {children}
    </RmsProviderErrorBoundary>
  );
}
